import React, { useState, useEffect } from 'react';
import { Form, Button, Col, Row } from 'react-bootstrap';
import DateHelper from 'utils/DateHelper';
import { addMinutes } from 'date-fns';
import ConfirmButton from 'components/ConfirmButton';
import { NorwegianDatepicker } from 'components/NorwegianDatepicker';
import { IcebergButton } from '@tradesolution/iceberg-ui-react';

export interface KundeNummerPeriodeFormData {
  kundenummer: string;
  eksisterendeFraOgMedDato: Date;
  fraOgMedDato: Date;
  tilOgMedDato?: Date;
}

interface Props {
  kundenummer: string;
  isEditMode: boolean;
  tilOgMedDato: Date;
  fraOgMedDato: Date;
  handleUpdateKjedeInfo: (data: KundeNummerPeriodeFormData) => void;
  handleFjernEnhetFromKjede?: () => Promise<void>;
  canRemoveKjede: boolean;
  hasActiveGruppering?: boolean;
}

const KundeNummerPeriodeForm: React.FC<Props> = (props) => {
  const eksisterendeFraOgMedDato = addMinutes(
    props.fraOgMedDato,
    -props.fraOgMedDato.getTimezoneOffset(),
  );

  const [fraOgMedDatoChanged, setFraOgMedDatoChanged] = useState(false);

  const [values, setValues] = useState<KundeNummerPeriodeFormData>({
    kundenummer: props.kundenummer || '',
    eksisterendeFraOgMedDato: eksisterendeFraOgMedDato || undefined,
    fraOgMedDato: props.fraOgMedDato,
    tilOgMedDato: props.tilOgMedDato,
  });

  // Only sync props to state when NOT in edit mode to avoid overwriting user's changes
  useEffect(() => {
    if (!props.isEditMode) {
      setValues({
        kundenummer: props.kundenummer,
        eksisterendeFraOgMedDato,
        tilOgMedDato: props.tilOgMedDato,
        fraOgMedDato: props.fraOgMedDato,
      });
      setFraOgMedDatoChanged(false);
    }
  }, [props.kundenummer, props.tilOgMedDato, props.fraOgMedDato, props.isEditMode]);

  const onSaveClick = () => {
    props.handleUpdateKjedeInfo(values);
  };

  const onFjernConfirmed = async () => {
    props.handleFjernEnhetFromKjede();
  };

  const handleFraOgMedDatoChange = (date) => {
    setValues((prevValues) => ({
      ...prevValues,
      fraOgMedDato: date,
    }));
    setFraOgMedDatoChanged(true);
  };

  const handleTilOgMedDatoChange = (date) => {
    setValues((prevValues) => ({
      ...prevValues,
      tilOgMedDato: date,
    }));
  };

  const handleKundenummerChange = (value: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      kundenummer: value,
    }));
  };

  return (
    <div>
      <Form onSubmit={(e) => e.preventDefault()}>
        <Form.Group>
          <Form.Label>Inn og utdato</Form.Label>
          {!props.isEditMode ? (
            <Form.Control
              disabled
              plaintext
              value={`${DateHelper.formatDate(props.fraOgMedDato)} - ${
                props.tilOgMedDato ? DateHelper.formatDate(props.tilOgMedDato) : ''
              }`}
            />
          ) : (
            <Row>
              <Form.Group as={Col}>
                <NorwegianDatepicker
                  value={values.fraOgMedDato}
                  onChange={(date) => handleFraOgMedDatoChange(date)}
                  placeholder="dd.mm.yyyy"
                  showFirstDaySuggestions
                />
              </Form.Group>
              <Form.Group as={Col}>
                <NorwegianDatepicker
                  value={values.tilOgMedDato}
                  onChange={(date) => handleTilOgMedDatoChange(date)}
                  placeholder="dd.mm.yyyy"
                  showLastDaySuggestions
                />
              </Form.Group>
            </Row>
          )}
          {fraOgMedDatoChanged && props.hasActiveGruppering && (
            <div className="text-warning mt-1" style={{ fontSize: '0.875em' }}>
              Husk å sjekke om datoene for enhetsgruppering må justeres.
            </div>
          )}
        </Form.Group>
        <Form.Group>
          <Form.Label>Avdelingsnummer</Form.Label>
          <Form.Control
            disabled={!props.isEditMode}
            plaintext={!props.isEditMode}
            value={values.kundenummer}
            onChange={(e) => handleKundenummerChange(e.target.value)}
          />
        </Form.Group>
        {props.isEditMode && (
          <Form.Group>
            <ConfirmButton
              onClick={onFjernConfirmed}
              disabled={!props.canRemoveKjede || !onFjernConfirmed}
            />
            <IcebergButton
              type="button"
              variant="primary"
              icon="disk"
              style={{ margin: '.5em' }}
              className="float-right"
              onClick={onSaveClick}
            >
              Lagre
            </IcebergButton>
          </Form.Group>
        )}
      </Form>
    </div>
  );
};

export default KundeNummerPeriodeForm;
